import { Box, Checkbox, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form"

import MultiSelect from "@/components/MultiSelect/MultiSelect"
import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES, SELECT_STYLING_PROPS } from "@/constants/forms"
import type { Location } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface LocationSelectProps {
  companyId: string
  name?: string
  label?: string
  disabled?: boolean
  multiple?: boolean
}

const CertificationLocationSelect: FC<LocationSelectProps> = ({
  companyId,
  name = "location",
  disabled,
  multiple,
  label = "Location",
}) => {
  const { control } = useFormContext()
  const { locations } = useGetLocations(companyId, {
    isLocationActive: true,
  })

  const renderLocationValue = (location: Location) => {
    return (
      <Typography variant='body1' color='textSecondary'>
        <span className='text-gray-900'>{location.name}</span> -{" "}
        {formatLocationAddress(location, AddressFormat.AddressOnly)}
      </Typography>
    )
  }

  const locationsOptions = useMemo(() => locations.map((v) => ({ value: v.locationId, label: v.name })), [locations])

  if (multiple) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box className='mt-6' data-testid='locations-company-certification-form'>
            <InputLabel id='locations' className={FIELD_LABEL_CLASSES}>
              {label} <span className='text-red-600'>*</span>
            </InputLabel>

            <MultiSelect
              name={field.name}
              value={field.value?.map(String) ?? []}
              onChange={field.onChange}
              options={locationsOptions}
              error={!!error}
            >
              {locations.map((location) => (
                <MenuItem key={location.locationId} value={location.locationId}>
                  <Checkbox checked={field.value?.map(String).includes(location.locationId)} />
                  <Typography variant='body1' color='textSecondary'>
                    <span className='text-gray-900'>{location.name}</span> -{" "}
                    {formatLocationAddress(location, AddressFormat.AddressOnly)}
                  </Typography>
                </MenuItem>
              ))}
            </MultiSelect>

            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </Box>
        )}
      />
    )
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth className='mt-6' data-testid='locations-company-certification-form'>
          <InputLabel id='locations' className={FIELD_LABEL_CLASSES}>
            {label} <span className='text-red-600'>*</span>
          </InputLabel>
          <Select
            {...field}
            {...SELECT_STYLING_PROPS}
            disabled={disabled}
            labelId='locations'
            error={!!error}
            value={field.value}
            renderValue={(value) => {
              if (!value) {
                return <span className='text-gray-500'>Select One</span>
              }
              const location = locations.find((e) => e.locationId === value)
              return location ? renderLocationValue(location) : ""
            }}
          >
            {locations.map((location) => (
              <MenuItem key={location.locationId} value={location.locationId}>
                {renderLocationValue(location)}
              </MenuItem>
            ))}
          </Select>
          {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
        </FormControl>
      )}
    />
  )
}

export default CertificationLocationSelect
